import { useEffect } from 'react';
import { useFormContext, FormFieldsData } from 'framework/form';
import { useEmployeeService } from 'domain/shared/employee';
import { useProjectTeamService } from 'domain/shared/newProject';
import { PROJECT_MEMBERSHIP_ROLES } from 'domain/shared/core/constants';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { useAsyncValue } from 'framework/utils';

const useAssignmentMembership = (isCreateForm?: boolean) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const { getProjectTeamByProjectId } = useProjectTeamService();

  const { getById } = useEmployeeService();
  const { data: formData, setFormData } = useFormContext();
  const employeeId = formData?.employeeId;
  const targetId = formData?.target?.id;
  const isAssignmentTeamMembershipEnabled = isFeatureEnabled(Features.ASSIGNMENT_TEAM_MEMBERSHIP);

  const showTeamMembershipFields = formData?.target?.type === 'PROJECT' && isAssignmentTeamMembershipEnabled;

  const { ACCOUNT_MANAGER, ...projectRolesForDropdown } = PROJECT_MEMBERSHIP_ROLES;
  const teamResponse = useAsyncValue(async () => {
    if (!showTeamMembershipFields || !targetId) {
      return null;
    }

    return getProjectTeamByProjectId(targetId);
  }, [showTeamMembershipFields, targetId]);

  useEffect(() => {
    if (!isCreateForm || !setFormData) {
      return;
    }

    let cancelled = false;

    const resolveMembershipFields = async () => {
      if (!employeeId) {
        return { positionId: null, competencyLevel: null, technologyId: null };
      }
      const employee = await getById(employeeId);
      return {
        positionId: employee?.positionId,
        competencyLevel: employee?.specializationInfo?.competencyLevel,
        technologyId: employee?.specializationInfo?.technologyId,
      };
    };

    resolveMembershipFields()
      .then(fields => {
        if (!cancelled) {
          setFormData((prev: FormFieldsData) => ({ ...prev, ...fields }));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, isCreateForm]);

  return { showTeamMembershipFields, teamResponse, projectRolesForDropdown };
};

export default useAssignmentMembership;
